import React from 'react';
import { useTheme } from 'styled-components';
import ReactModal from 'react-modal';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { Button } from '../Button/Button';
import { CloseIcon } from '../icons/CloseIcon';
import {
  ContentWrapper,
  HeaderActions,
  InnerContainer,
  modalStyles,
  Name,
} from './InfoDrawer.styles';

type Props = {
  title: string;
  body: JSX.Element;
  isOpen: boolean;
  onCloseInfoDrawer?: () => void;
};

/**
 * @param {string} [title] - title
 * @param {JSX.Element} [body] - content of the drawer
 * @param {boolean} [isOpen] - is the info drawer open
 * @param {function} [onCloseInfoDrawer] - called when info drawer is closing (optional)
 */

export function InfoDrawer({ title, body, isOpen, onCloseInfoDrawer }: Props) {
  const theme = useTheme();

  const styles = modalStyles(theme);

  const handleCloseInfoDrawer = () => {
    onCloseInfoDrawer?.();
  };

  const closeIcon = () => <CloseIcon size="large" color={theme.colors.textPrimarySecondary1100} />;

  return (
    <ReactModal
      className="info-drawer__content"
      overlayClassName="info-drawer__overlay"
      contentLabel={`${title} Info Drawer`}
      isOpen={isOpen}
      preventScroll={false}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      onRequestClose={handleCloseInfoDrawer}
      appElement={document.querySelector('#root') as HTMLElement}
      style={styles}
      closeTimeoutMS={300} // matches transition duration defined in GlobalStyles
    >
      <InnerContainer onClick={handleCloseInfoDrawer}>
        <ContentWrapper
          onClick={(event) => {
            event.stopPropagation(); // Prevent triggering InnerContainer's onClick for all child clicks
          }}
        >
          <SwitchTransition mode="out-in">
            <CSSTransition key={title} timeout={300} classNames="slide" unmountOnExit>
              <>
                <HeaderActions>
                  <Button
                    styleVariant="tertiary"
                    Icon={closeIcon}
                    margin="0 0 0 auto"
                    onClick={handleCloseInfoDrawer}
                  />
                </HeaderActions>
                <Name as="h3">{title}</Name>

                {body}
              </>
            </CSSTransition>
          </SwitchTransition>
        </ContentWrapper>
      </InnerContainer>
    </ReactModal>
  );
}

export default InfoDrawer;
